import { useState, useRef, useCallback } from 'react';
import { Upload } from 'lucide-react';

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/tiff',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
  'video/x-matroska',
];
const MAX_SIZE = 200 * 1024 * 1024; // 200MB

interface PhotoUploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

export function PhotoUploadDropzone({ onFilesSelected, disabled }: PhotoUploadDropzoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndSubmit = useCallback(
    (files: FileList | File[]) => {
      setError('');
      const valid: File[] = [];
      for (const file of Array.from(files)) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          setError(`"${file.name}" is not a supported file type`);
          return;
        }
        if (file.size > MAX_SIZE) {
          setError(`"${file.name}" exceeds 200 MB limit`);
          return;
        }
        valid.push(file);
      }
      if (valid.length > 0) onFilesSelected(valid);
    },
    [onFilesSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      if (!disabled && e.dataTransfer.files.length > 0) {
        validateAndSubmit(e.dataTransfer.files);
      }
    },
    [disabled, validateAndSubmit],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setDragActive(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(() => setDragActive(false), []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        validateAndSubmit(e.target.files);
        e.target.value = '';
      }
    },
    [validateAndSubmit],
  );

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Upload className="mx-auto mb-2 text-gray-400" size={28} />
        <p className="text-sm text-gray-600">
          Drag & drop photos or videos here, or{' '}
          <span className="text-primary-600 font-medium">browse</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Images (JPEG, PNG, WebP, GIF, HEIC) &amp; Videos (MP4, MOV, WebM) up to 200 MB
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
          multiple
          onChange={handleInputChange}
          className="hidden"
        />
      </div>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
